'use client';

import { c as _c } from "react/compiler-runtime";
import { Map as GoogleMap, Marker, Polyline } from '@vis.gl/react-google-maps';
import { useDotEnv } from '@repo/dot-env/client';
import { kmToMiles } from '@repo/sdk/helpers';
import { ComponentProps, ReactNode } from 'react';
import { DefaultRouteMarker } from './default-route-marker.tsx';
import { RouteMapBounds } from './route-map-bounds.tsx';
import { getRoutePreviewPoint, type RoutePreviewPoint, type RoutePreviewStop } from './route-preview.ts';
import { useRoutePreview } from './use-route-preview.ts';
export type RouteMapMarkerProps = {
  drop: RoutePreviewStop;
  index: number;
  point: RoutePreviewPoint;
};
type Props = Omit<ComponentProps<typeof GoogleMap>, 'children'> & {
  drops: RoutePreviewStop[];
  renderMarker?: (marker: RouteMapMarkerProps) => ReactNode;
  showEstimatedTrip?: boolean;
};
const DEFAULT_CENTER = {
  lat: 54,
  lng: -2
};
const DEFAULT_ZOOM = 7;
export const RouteMap = t0 => {
  const $ = _c(62);
  let drops;
  let mapId;
  let props;
  let renderMarker;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== t0) {
    ({
      defaultCenter: t1,
      defaultZoom: t2,
      drops,
      mapId,
      minZoom: t3,
      renderMarker,
      showEstimatedTrip: t4,
      streetViewControl: t5,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = drops;
    $[2] = mapId;
    $[3] = props;
    $[4] = renderMarker;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    drops = $[1];
    mapId = $[2];
    props = $[3];
    renderMarker = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
  }
  const defaultCenter = t1 === undefined ? DEFAULT_CENTER : t1;
  const defaultZoom = t2 === undefined ? DEFAULT_ZOOM : t2;
  const minZoom = t3 === undefined ? 3 : t3;
  const showEstimatedTrip = t4 === undefined ? false : t4;
  const streetViewControl = t5 === undefined ? false : t5;
  const env = useDotEnv();
  const {
    encodedPaths,
    points,
    totalDistance
  } = useRoutePreview(drops);
  const resolvedMapId = mapId ?? env.GOOGLE_MAP_ID;
  if (!env.GOOGLE_MAPS_API_KEY) {
    return null;
  }
  let T0;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[10] !== defaultCenter || $[11] !== defaultZoom || $[12] !== drops || $[13] !== minZoom || $[14] !== points || $[15] !== props || $[16] !== renderMarker || $[17] !== resolvedMapId || $[18] !== streetViewControl) {
    const markerStops = drops.map(_temp).filter(_temp2);
    let t17;
    if ($[31] !== renderMarker || $[32] !== resolvedMapId) {
      t17 = markerStop_0 => {
        if (renderMarker) {
          return renderMarker(markerStop_0);
        }
        if (resolvedMapId) {
          return <DefaultRouteMarker key={`${markerStop_0.point.lat}-${markerStop_0.point.lng}-${markerStop_0.index}`} {...markerStop_0} />;
        }
        return <Marker key={`${markerStop_0.point.lat}-${markerStop_0.point.lng}-${markerStop_0.index}`} position={markerStop_0.point} data-sentry-element="Marker" data-sentry-component="renderRouteMarker" data-sentry-source-file="route-map.tsx" />;
      };
      $[31] = renderMarker;
      $[32] = resolvedMapId;
      $[33] = t17;
    } else {
      t17 = $[33];
    }
    const renderRouteMarker = t17;
    T0 = GoogleMap;
    t6 = defaultCenter;
    t7 = defaultZoom;
    t8 = resolvedMapId;
    t9 = minZoom;
    t10 = true;
    t11 = streetViewControl;
    t12 = props;
    t13 = "GoogleMap";
    t14 = "route-map.tsx";
    if ($[34] !== defaultCenter || $[35] !== defaultZoom || $[36] !== points) {
      t15 = <RouteMapBounds defaultCenter={defaultCenter} defaultZoom={defaultZoom} points={points} data-sentry-element="RouteMapBounds" data-sentry-source-file="route-map.tsx" />;
      $[34] = defaultCenter;
      $[35] = defaultZoom;
      $[36] = points;
      $[37] = t15;
    } else {
      t15 = $[37];
    }
    let t18;
    if ($[38] !== renderRouteMarker) {
      t18 = markerStop_1 => renderRouteMarker(markerStop_1);
      $[38] = renderRouteMarker;
      $[39] = t18;
    } else {
      t18 = $[39];
    }
    t16 = markerStops.map(t18);
    $[10] = defaultCenter;
    $[11] = defaultZoom;
    $[12] = drops;
    $[13] = minZoom;
    $[14] = points;
    $[15] = props;
    $[16] = renderMarker;
    $[17] = resolvedMapId;
    $[18] = streetViewControl;
    $[19] = T0;
    $[20] = t10;
    $[21] = t11;
    $[22] = t12;
    $[23] = t13;
    $[24] = t14;
    $[25] = t15;
    $[26] = t16;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
  } else {
    T0 = $[19];
    t10 = $[20];
    t11 = $[21];
    t12 = $[22];
    t13 = $[23];
    t14 = $[24];
    t15 = $[25];
    t16 = $[26];
    t6 = $[27];
    t7 = $[28];
    t8 = $[29];
    t9 = $[30];
  }
  let t17;
  if ($[40] !== encodedPaths) {
    t17 = encodedPaths.map(_temp3);
    $[40] = encodedPaths;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== T0 || $[43] !== t10 || $[44] !== t11 || $[45] !== t12 || $[46] !== t13 || $[47] !== t14 || $[48] !== t15 || $[49] !== t16 || $[50] !== t17 || $[51] !== t6 || $[52] !== t7 || $[53] !== t8 || $[54] !== t9) {
    t18 = <T0 defaultCenter={t6} defaultZoom={t7} mapId={t8} minZoom={t9} reuseMaps={t10} streetViewControl={t11} {...t12} data-sentry-element={t13} data-sentry-source-file={t14}>{t15}{t16}{t17}</T0>;
    $[42] = T0;
    $[43] = t10;
    $[44] = t11;
    $[45] = t12;
    $[46] = t13;
    $[47] = t14;
    $[48] = t15;
    $[49] = t16;
    $[50] = t17;
    $[51] = t6;
    $[52] = t7;
    $[53] = t8;
    $[54] = t9;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  let t19;
  if ($[56] !== showEstimatedTrip || $[57] !== totalDistance) {
    t19 = showEstimatedTrip && totalDistance && <><p className="my-4 mr-auto text-sm font-medium text-gray-700">Estimated trip:{" "}<label className="ml-2 text-base font-semibold text-gray-800">{kmToMiles(totalDistance / 1000)} miles</label></p><hr className="w-full" /></>;
    $[56] = showEstimatedTrip;
    $[57] = totalDistance;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  let t20;
  if ($[59] !== t18 || $[60] !== t19) {
    t20 = <>{t18}{t19}</>;
    $[59] = t18;
    $[60] = t19;
    $[61] = t20;
  } else {
    t20 = $[61];
  }
  return t20;
};
function _temp(drop, index) {
  const point = getRoutePreviewPoint(drop);
  if (!point) {
    return null;
  }
  return {
    drop,
    index,
    point
  };
}
function _temp2(markerStop) {
  return markerStop !== null;
}
function _temp3(encodedPath, index_0) {
  return <Polyline clickable={false} encodedPath={encodedPath} key={`${encodedPath}-${index_0}`} strokeColor="#000000" strokeOpacity={1} strokeWeight={4} />;
}